import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../../styles/auth.css';
import logoPng from '../../assets/images/logo.png';
import { forgotRequest } from '../../services/password';
import { toast } from 'react-toastify';

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

export default function ForgotPassword() {
  const [email,      setEmail]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent,       setSent]       = useState(false);
  const [error,      setError]      = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await forgotRequest(email);
      setSent(true);
      toast.success('Reset code sent! Check your inbox.');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not send reset email. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    navigate('/email-verification', { state: { email, mode: 'forgot-password' } });
  };

  return (
    <div className="auth-page">

      {/* ── Left panel ── */}
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-left-logo auth-left-logo-xl">
            <img src={logoPng} alt="Easy World Online" />
          </div>
          <h1 className="auth-left-title">Reset your<br/>password.</h1>
          <p className="auth-left-sub">
            Enter your registered email and we'll send you a secure code to get back into your account.
          </p>
          <div className="auth-left-pills">
            <span className="auth-pill">🔒 Secure Reset</span>
            <span className="auth-pill">📧 Email Verification</span>
            <span className="auth-pill">⚡ Quick Recovery</span>
          </div>
        </div>
        <div className="auth-orb auth-orb-1"/>
        <div className="auth-orb auth-orb-2"/>
        <div className="auth-orb auth-orb-3"/>
      </div>

      {/* ── Right panel ── */}
      <div className="auth-right">
        <div className="auth-card forgot-card-layout">

          {!sent ? (
            /* ── Request form ── */
            <>
              <div className="auth-card-header">
                <div className="auth-card-logo">
                  <img src={logoPng} alt="Easy World"/>
                </div>
                <div>
                  <h2 className="auth-card-title">Forgot password?</h2>
                  <p className="auth-card-sub">We'll send a reset code to your email</p>
                </div>
              </div>

              {/* Illustration strip */}
              <div className="fp-illustration">
                <div className="fp-illustration-icon">
                  <ShieldIcon/>
                </div>
                <div className="fp-illustration-text">
                  <div className="fp-illustration-title">Secure password recovery</div>
                  <div className="fp-illustration-sub">A 6-digit code will be sent to your inbox. It expires in 15 minutes.</div>
                </div>
              </div>

              {error && (
                <div className="auth-error">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              <form className="auth-form" onSubmit={handleSubmit} style={{ marginBottom: 0 }}>
                <div className="auth-field">
                  <label className="auth-label">Email Address</label>
                  <div className="auth-input-wrap">
                    <span className="auth-input-icon"><MailIcon/></span>
                    <input
                      type="email"
                      className="auth-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                      disabled={submitting}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn-primary"
                  disabled={submitting}
                  style={{ marginTop: 8 }}
                >
                  {submitting
                    ? <><span className="auth-btn-spinner"/>&nbsp;Sending code…</>
                    : <>Send Reset Code &nbsp;<ArrowIcon/></>}
                </button>
              </form>

              <p className="auth-footer-text" style={{ marginTop: 20 }}>
                Remember your password?{' '}
                <Link to="/login" className="auth-link">Sign in</Link>
              </p>
            </>
          ) : (
            /* ── Success state ── */
            <>
              <div className="fp-success">
                <div className="fp-success-icon">
                  <CheckIcon/>
                </div>
                <h2 className="fp-success-title">Check your inbox!</h2>
                <p className="fp-success-sub">
                  We sent a 6-digit reset code to
                </p>
                <div className="fp-success-email">{email}</div>
                <p className="fp-success-note">
                  Didn't receive it? Check your spam folder or{' '}
                  <button
                    className="fp-resend-btn"
                    onClick={() => setSent(false)}
                    type="button"
                  >
                    try a different email
                  </button>
                </p>

                <button
                  className="auth-btn auth-btn-primary"
                  onClick={handleContinue}
                  style={{ marginTop: 8 }}
                >
                  Enter the code &nbsp;<ArrowIcon/>
                </button>

                <Link to="/login" className="auth-btn auth-btn-ghost" style={{ marginTop: 10, textDecoration: 'none', display: 'flex' }}>
                  <BackIcon/>&nbsp; Back to login
                </Link>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}